import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ZLoadingBar from '@/components/shared/ZLoadingBar';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FramedCollection() {
  const { slug } = useParams();
  const [collection, setCollection] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/framed/collections/${slug}`)
      .then(r => {
        if (r.data.is_password_protected) {
          setNeedsPassword(true);
        } else {
          setCollection(r.data);
        }
      })
      .catch(() => setError('not found'));
  }, [slug]);

  const verifyPassword = async () => {
    try {
      await axios.post(`${API}/framed/collections/${slug}/verify`, { password });
      const { data } = await axios.get(`${API}/framed/collections/${slug}`);
      setCollection(data);
      setNeedsPassword(false);
    } catch {
      setError('invalid password');
    }
  };

  const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

  if (error === 'not found') return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl text-[#f0f0f5] font-serif mb-2">Not Found</h1>
        <p className="text-[#b0b4c0] font-light">This collection doesn't exist.</p>
      </div>
    </div>
  );

  if (needsPassword) return (
    <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center p-6" data-testid="collection-password-page">
      <div className="w-full max-w-xs text-center">
        <Lock size={24} className="mx-auto mb-4 text-[#9B6DD4]" />
        <h2 className="text-xl text-[#f0f0f5] font-serif mb-2">Protected Collection</h2>
        <p className="text-[#b0b4c0] font-light text-sm mb-6">Enter the password to view this collection.</p>
        {error === 'invalid password' && <p className="text-red-400 text-sm mb-4">Invalid password</p>}
        <Input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && verifyPassword()}
          className="bg-white/[0.05] border-white/[0.06] text-[#f0f0f5] mb-4"
          data-testid="collection-password-input"
        />
        <Button onClick={verifyPassword} className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full" data-testid="collection-password-submit">
          View Collection
        </Button>
      </div>
    </div>
  );

  if (!collection) return (
    <div className="min-h-screen bg-[#0a0a0f]">
      <div style={{ padding: '48px 24px' }}>
        <ZLoadingBar accent="#6B35B8" loading={true} />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0a0f]" data-testid="framed-collection-page">
      {/* Header */}
      <div className="relative h-[40vh] flex items-end">
        {collection.cover_image ? (
          <img src={getImageUrl(collection.cover_image)} alt="" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-b from-[#6B35B8]/15 to-[#0a0a0f]" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/30 to-transparent" />
        <div className="relative z-10 p-8 md:p-16">
          <h1 className="text-4xl sm:text-5xl text-white font-serif font-medium tracking-tight" data-testid="collection-title">
            {collection.title}
          </h1>
          {collection.description && <p className="text-white/60 font-light mt-2">{collection.description}</p>}
        </div>
      </div>

      {/* Collection Entries */}
      <div className="max-w-5xl mx-auto px-6 md:px-16 py-16 space-y-20">
        {collection.entries?.map((entry, i) => {
          if (entry.media_type === 'photo' || (entry.images && entry.images.length > 0)) {
            const imgUrl = entry.images?.[0] ? getImageUrl(entry.images[0]) : null;
            return (
              <div key={entry.id || i} className="animate-fade-in">
                {imgUrl && (
                  <img src={imgUrl} alt={entry.title || ''} className="w-full rounded-xl object-cover" />
                )}
                {entry.content && (
                  <p className="text-[#b0b4c0] font-light text-sm mt-4 text-center max-w-xl mx-auto">{entry.content}</p>
                )}
              </div>
            );
          }

          if (entry.media_type === 'quote') {
            return (
              <div key={entry.id || i} className="py-12 animate-fade-in">
                <blockquote className="text-3xl md:text-4xl text-[#f0f0f5] font-light leading-relaxed italic font-serif text-center max-w-3xl mx-auto">
                  "{entry.quote_text || entry.content}"
                </blockquote>
                {entry.quote_author && (
                  <p className="text-[#9B6DD4] text-sm mt-6 font-mono tracking-wider text-center">-- {entry.quote_author}</p>
                )}
              </div>
            );
          }

          // Highlight / text waypoint
          return (
            <div key={entry.id || i} className="text-center py-8 animate-fade-in">
              {entry.location && (
                <p className="text-[#b0b4c0]/70 text-xs font-mono tracking-[0.2em] uppercase mb-3">{entry.location}</p>
              )}
              <p className="text-xl md:text-2xl text-[#f0f0f5]/70 font-light font-serif italic max-w-2xl mx-auto">
                {entry.content || entry.title}
              </p>
            </div>
          );
        })}
      </div>

      <div className="text-center py-16">
        <p className="text-[#b0b4c0]/20 text-xs font-mono tracking-[0.3em]">WERNER LABS</p>
      </div>
    </div>
  );
}
